// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { AddOutlined, CloseOutlined } from '@/constants/hardfin-standard-icons';
import {
  BillingPlanFormValues,
  buildEmptyMilestonePhases,
} from '@/project/components/billing-plan-constants';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

interface Props {
  control: Control<BillingPlanFormValues>;
  setValue: UseFormSetValue<BillingPlanFormValues>;
  phases: BillingPlanFormValues['milestonePhases'];
}

/**
 * Editor for the milestone_phases wrapper on a billing plan. Renders a
 * variable-length list of rows with a name text field and a percentage
 * field. Add / remove buttons manage the array; the inline total row
 * surfaces a warning when the sum diverges from 100%.
 *
 * Phases are informational — they describe the intended breakdown but are
 * not linked to any specific charge row. Users can edit freely at any
 * time; the charge schedule only refreshes when the user explicitly
 * clicks Regenerate, and that button is disabled at the drawer level
 * whenever any existing charge has been invoiced.
 */
export function MilestonePhasesEditor(props: Props) {
  const { phases, setValue } = props;

  // Seed an empty phases blob when the section mounts without one — protects
  // the required-length invariant at save time and gives the user something
  // to start editing immediately.
  useEffect(() => {
    if (phases !== null && phases !== undefined) return;
    setValue('milestonePhases', buildEmptyMilestonePhases(), {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [phases, setValue]);

  if (!phases) {
    return null;
  }

  const entries = phases.entries;
  const sum = phases.entries.reduce((acc, entry) => acc + entry.percentage, 0);
  const delta = sum - 100;
  const isOutOfBalance = Math.abs(delta) > 0.001;

  const handleAddPhase = () => {
    setValue(
      'milestonePhases',
      {
        mode: 'PERCENTAGE',
        entries: [...phases.entries, { name: '', percentage: 0 }],
      },
      { shouldDirty: true, shouldTouch: true }
    );
  };

  const handleRemovePhase = (indexToRemove: number) => {
    if (entries.length <= 1) return;
    setValue(
      'milestonePhases',
      {
        mode: 'PERCENTAGE',
        entries: phases.entries.filter((_, i) => i !== indexToRemove),
      },
      { shouldDirty: true, shouldTouch: true }
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Phases
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
        {entries.map((_entry, index) => (
          // Keyed by index to keep react-hook-form's per-field path
          // subscriptions (`milestonePhases.entries.${index}.*`) aligned
          // with React's reconciliation. Using an entry-level key would
          // cause surviving rows to shift paths on delete while RHF
          // holds stale subscriptions, producing values pinned to the
          // old row.
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Controller
              control={props.control}
              name={`milestonePhases.entries.${index}.name`}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  placeholder="Phase name"
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={props.control}
              name={`milestonePhases.entries.${index}.percentage`}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? 0}
                  onChange={(event) => {
                    const parsed = Number(event.target.value);
                    field.onChange(Number.isNaN(parsed) ? 0 : parsed);
                  }}
                  size="small"
                  type="number"
                  inputProps={{ step: '0.01', min: 0, max: 100 }}
                  sx={{ width: 140 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  error={Boolean(fieldState.error)}
                />
              )}
            />
            <IconButton
              size="small"
              onClick={() => handleRemovePhase(index)}
              disabled={entries.length <= 1}
              aria-label="Remove phase"
            >
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 1 }}>
        <Button
          size="small"
          startIcon={<AddOutlined />}
          onClick={handleAddPhase}
          sx={{ textTransform: 'none' }}
        >
          Add phase
        </Button>
      </Box>
      <Box
        sx={{
          mt: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Total
        </Typography>
        <Typography
          variant="caption"
          color={isOutOfBalance ? 'warning.main' : 'text.secondary'}
          sx={{ fontWeight: 600 }}
        >
          {`${sum.toFixed(2)}%`}
          {isOutOfBalance && (
            <> ({`${delta >= 0 ? '+' : ''}${delta.toFixed(2)}% from 100%`})</>
          )}
        </Typography>
      </Box>
    </Box>
  );
}
